import type { LevelConfig, LevelLayer, LevelPiece, LevelPieceType } from "../level_config.js";
import { TYPES } from "./block_showcase.js";
import { BlockMassTuner } from "./block_mass_tuner.js";

const SUPER_HARD = 2;

const COLUMNS = 9;
const ROWS = 5;
const LAYERS = 3;

interface TextureSpec
{
  name: string;
  entity: string;
  type: number;
  variant?: number;
}

function buildSpecs(): TextureSpec[]
{
  const specs: TextureSpec[] = [];

  for ( const type of TYPES )
  {
    for ( let v = 0; v < type.variants; v++ )
    {
      if ( type.skip?.includes( v ) )
        continue;

      specs.push( {
        name: type.variants > 1 ? `${type.name} v${v}` : type.name,
        entity: type.entity,
        type: type.type,
        variant: type.variants > 1 ? v : undefined,
      } );
    }
  }

  return specs;
}

const SPECS = buildSpecs();

function buildLayer( spec: TextureSpec ): LevelLayer
{
  const grid: number[][] = Array.from( { length: ROWS }, () => Array( COLUMNS ).fill( 0 ) );
  const pieces: LevelPiece[] = [];
  let id = 1;

  for ( let y = 0; y < ROWS; y++ )
  {
    for ( let x = 0; x < COLUMNS; x++ )
    {
      grid[y][x] = id;
      pieces.push( {
        id,
        x,
        y,
        width: 1,
        height: 1,
        type: spec.type as LevelPieceType,
        variant: spec.variant,
      } );
      id++;
    }
  }

  return { grid, pieces };
}

function buildConfig( index: number ): LevelConfig
{
  const spec = SPECS[index];

  return {
    level: TextureTest.BASE_LEVEL + index,
    difficulty: SUPER_HARD as LevelConfig["difficulty"],
    layers: Array.from( { length: LAYERS }, () => buildLayer( spec ) ),
  };
}

export class TextureTest
{
  public static readonly BASE_LEVEL = 90200;
  public static readonly COUNT = SPECS.length;
  public static readonly BLOCKS_PER_LEVEL = COLUMNS * ROWS * LAYERS;

  public static show_level: ( ( level: number ) => void ) | null = null;

  private static active = false;
  private static index = 0;
  private static configs: ( LevelConfig | null )[] = SPECS.map( () => null );

  private static root: HTMLDivElement | null = null;
  private static caption: HTMLDivElement | null = null;
  private static mass_slider: HTMLInputElement | null = null;
  private static mass_field: HTMLInputElement | null = null;
  private static mass_note: HTMLSpanElement | null = null;

  public static isActive(): boolean
  {
    return this.active;
  }

  public static isTextureLevel( level: number ): boolean
  {
    return level >= this.BASE_LEVEL && level < this.BASE_LEVEL + this.COUNT;
  }

  public static getIndex(): number
  {
    return this.index;
  }

  public static getName( index: number = this.index ): string
  {
    return SPECS[( index + this.COUNT ) % this.COUNT].name;
  }

  public static getEntity( index: number = this.index ): string
  {
    return SPECS[( index + this.COUNT ) % this.COUNT].entity;
  }

  public static names(): string[]
  {
    return SPECS.map( s => s.name );
  }

  public static currentLevel(): number
  {
    return this.BASE_LEVEL + this.index;
  }

  public static activate( index: number ): number
  {
    this.active = true;
    this.index = ( ( index % this.COUNT ) + this.COUNT ) % this.COUNT;
    return this.currentLevel();
  }

  public static advance(): number
  {
    return this.activate( this.index + 1 );
  }

  public static back(): number
  {
    return this.activate( this.index - 1 );
  }

  public static deactivate(): void
  {
    this.active = false;
    BlockMassTuner.setActiveKey( null );
    this.removeOverlay();
  }

  public static hideBar(): void
  {
    this.removeOverlay();
  }

  public static beginLevel( level: number ): void
  {
    this.active = true;
    this.index = level - this.BASE_LEVEL;
    BlockMassTuner.setActiveKey( this.getName() );
    this.buildOverlay();
  }

  public static resetMasses(): void
  {
    BlockMassTuner.resetAll();
    this.reloadLevel();
  }

  private static reloadLevel(): void
  {
    this.syncMassControls();
    this.show_level?.( this.currentLevel() );
  }

  public static setBlockMass( mass: number ): void
  {
    this.setMass( mass );
    this.reloadLevel();
  }

  private static setMass( mass: number ): void
  {
    BlockMassTuner.setMass( mass );
    this.syncMassControls();
  }

  private static syncMassControls(): void
  {
    const base = BlockMassTuner.getBaseMass();
    const mass = BlockMassTuner.getMass();

    if ( this.mass_slider )
    {
      this.mass_slider.disabled = mass === null;
      this.mass_slider.value = String(
        mass === null
          ? 0
          : Math.min( BlockMassTuner.MASS_MAX, Math.max( BlockMassTuner.MASS_MIN, mass ) )
      );
    }

    if ( this.mass_field && this.mass_field !== document.activeElement )
    {
      this.mass_field.disabled = mass === null;
      this.mass_field.value = mass === null ? "" : String( Number( mass.toFixed( 2 ) ) );
    }

    if ( this.mass_note )
      this.mass_note.textContent = base === null
        ? "база ?"
        : `база ${Number( base.toFixed( 2 ) )} · ×${BlockMassTuner.getMultiplier().toFixed( 2 )}`;
  }

  public static getConfig( level: number ): LevelConfig | null
  {
    if ( !this.isTextureLevel( level ) )
      return null;

    const index = level - this.BASE_LEVEL;

    if ( this.configs[index] === null )
      this.configs[index] = buildConfig( index );

    return this.configs[index];
  }

  private static go( delta: number ): void
  {
    const level = this.activate( this.index + delta );
    BlockMassTuner.setActiveKey( this.getName() );
    this.updateCaption();
    this.syncMassControls();
    this.show_level?.( level );
  }

  private static updateCaption(): void
  {
    if ( !this.caption )
      return;

    this.caption.innerHTML = "";

    const title = document.createElement( "div" );
    title.style.fontSize = "15px";
    title.style.fontWeight = "700";
    title.textContent = `${this.index + 1}/${this.COUNT}  ${this.getName()}`;

    const sub = document.createElement( "div" );
    sub.style.fontSize = "11px";
    sub.style.opacity = "0.75";
    sub.textContent = `${this.getEntity()} · ${TextureTest.BLOCKS_PER_LEVEL} шт`;

    this.caption.appendChild( title );
    this.caption.appendChild( sub );
  }

  private static removeOverlay(): void
  {
    BlockMassTuner.setBaseListener( null );
    this.root?.remove();
    this.root = null;
    this.caption = null;
    this.mass_slider = null;
    this.mass_field = null;
    this.mass_note = null;
  }

  private static buildOverlay(): void
  {
    this.removeOverlay();

    const root = document.createElement( "div" );
    root.id = "textureTestBar";
    root.style.position = "fixed";
    root.style.left = "0";
    root.style.right = "0";
    root.style.bottom = "calc(56px + env(safe-area-inset-bottom, 0px))";
    root.style.zIndex = "2147483000";
    root.style.display = "flex";
    root.style.alignItems = "stretch";
    root.style.gap = "8px";
    root.style.padding = "8px 8px 8px 104px";
    root.style.boxSizing = "border-box";
    root.style.background = "rgba(10, 12, 20, 0.82)";
    root.style.font = "13px Arial, sans-serif";
    root.style.color = "#fff";
    root.style.touchAction = "none";

    const stop = ( event: Event ) => event.stopPropagation();
    for ( const name of [ "pointerdown", "pointermove", "pointerup", "click" ] )
      root.addEventListener( name, stop );

    const button = ( text: string, action: () => void ): HTMLButtonElement =>
    {
      const el = document.createElement( "button" );
      el.type = "button";
      el.textContent = text;
      el.style.minWidth = "56px";
      el.style.minHeight = "56px";
      el.style.fontSize = "20px";
      el.style.fontWeight = "700";
      el.style.color = "#fff";
      el.style.background = "#1e88e5";
      el.style.border = "none";
      el.style.borderRadius = "8px";
      el.addEventListener( "click", action );
      return el;
    };

    const caption = document.createElement( "div" );
    caption.style.flex = "1";
    caption.style.minWidth = "0";
    caption.style.display = "flex";
    caption.style.flexDirection = "column";
    caption.style.alignItems = "center";
    caption.style.justifyContent = "center";
    caption.style.textAlign = "center";
    caption.style.lineHeight = "1.25";

    const picker = document.createElement( "select" );
    picker.style.minHeight = "56px";
    picker.style.maxWidth = "110px";
    picker.style.fontSize = "13px";
    picker.style.color = "#fff";
    picker.style.background = "#263238";
    picker.style.border = "none";
    picker.style.borderRadius = "8px";

    SPECS.forEach( ( spec, i ) =>
    {
      const option = document.createElement( "option" );
      option.value = String( i );
      option.textContent = `${i + 1}. ${spec.name}`;
      picker.appendChild( option );
    } );

    picker.value = String( this.index );
    picker.addEventListener( "change", () =>
    {
      this.go( Number( picker.value ) - this.index );
    } );

    const mass_box = document.createElement( "div" );
    mass_box.style.display = "flex";
    mass_box.style.flexDirection = "column";
    mass_box.style.justifyContent = "center";
    mass_box.style.gap = "3px";
    mass_box.style.minWidth = "128px";

    const mass_head = document.createElement( "div" );
    mass_head.style.display = "flex";
    mass_head.style.alignItems = "center";
    mass_head.style.justifyContent = "space-between";
    mass_head.style.gap = "6px";

    const mass_title = document.createElement( "span" );
    mass_title.textContent = "МАССА";
    mass_title.style.fontSize = "11px";
    mass_title.style.opacity = "0.7";

    const mass_field = document.createElement( "input" );
    mass_field.type = "number";
    mass_field.step = "0.5";
    mass_field.style.width = "60px";
    mass_field.style.padding = "3px 4px";
    mass_field.style.borderRadius = "4px";
    mass_field.style.border = "1px solid rgba(255,255,255,0.25)";
    mass_field.style.background = "rgba(255,255,255,0.08)";
    mass_field.style.color = "#fff";
    mass_field.style.font = "700 12px Arial, sans-serif";
    mass_field.style.textAlign = "right";

    const mass_slider = document.createElement( "input" );
    mass_slider.type = "range";
    mass_slider.min = String( BlockMassTuner.MASS_MIN );
    mass_slider.max = String( BlockMassTuner.MASS_MAX );
    mass_slider.step = "0.5";
    mass_slider.style.width = "100%";

    const mass_note = document.createElement( "span" );
    mass_note.style.fontSize = "10px";
    mass_note.style.opacity = "0.55";

    mass_head.appendChild( mass_title );
    mass_head.appendChild( mass_field );
    mass_box.appendChild( mass_head );
    mass_box.appendChild( mass_slider );
    mass_box.appendChild( mass_note );

    mass_slider.addEventListener( "input", () => this.setMass( Number( mass_slider.value ) ) );
    mass_slider.addEventListener( "change", () => this.reloadLevel() );

    const commitField = (): void =>
    {
      const typed = Number( mass_field.value );
      if ( Number.isFinite( typed ) )
        this.setMass( typed );
      this.reloadLevel();
    };

    mass_field.addEventListener( "change", commitField );
    mass_field.addEventListener( "keydown", event =>
    {
      if ( ( event as KeyboardEvent ).key === "Enter" )
        commitField();
    } );

    const reset_btn = button( "⟳", () => this.resetMasses() );
    reset_btn.style.background = "#c62828";
    reset_btn.title = "Сбросить массы всех блоков в дефолт";

    root.appendChild( button( "‹", () => this.go( -1 ) ) );
    root.appendChild( caption );
    root.appendChild( mass_box );
    root.appendChild( reset_btn );
    root.appendChild( picker );
    root.appendChild( button( "›", () => this.go( 1 ) ) );

    document.body.appendChild( root );

    this.root = root;
    this.caption = caption;
    this.mass_slider = mass_slider;
    this.mass_field = mass_field;
    this.mass_note = mass_note;
    BlockMassTuner.setBaseListener( () => this.syncMassControls() );
    this.updateCaption();
    this.syncMassControls();
  }
}
